import type { NavigateFunction } from 'react-router';
import { ROUTES } from '@/shared/config/routes';
import type { ChatNavigationState } from '@/types/routing.types';
import { useChatStore } from '@/features/chats/stores';

export interface SendOrCreateChatParams {
  agentId: string;
  prompt: string;
  inputs?: Record<string, unknown>;
  /** When true, the message will be hidden from the chat UI */
  isHidden?: boolean;
}

/**
 * Sends a message to an existing chat session or creates a new one if none exists.
 *
 * @param navigate - React Router navigate function
 * @param params - Chat parameters (agentId, prompt, inputs)
 */
export async function sendOrCreateChat(
  navigate: NavigateFunction,
  params: SendOrCreateChatParams,
): Promise<void> {
  const { chatId, sendMessage, initializeChat, setAgentById } = useChatStore.getState();

  if (chatId) {
    await sendMessage({
      agentId: params.agentId,
      prompt: params.prompt,
      inputs: params.inputs,
      ...(params.isHidden && { isHidden: true }),
    });
  } else {
    const newChatId = await initializeChat(params.agentId);
    const route = ROUTES.CHAT_SESSION(newChatId);

    await setAgentById(params.agentId);

    void navigate(route, {
      state: {
        agentId: params.agentId,
        args: {
          agentId: params.agentId,
          prompt: params.prompt,
          inputs: params.inputs,
          ...(params.isHidden && { isHidden: true }),
        },
      } satisfies ChatNavigationState,
      replace: true,
    });
  }
}
